/* eslint-disable max-lines */
import {
  RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useMediaQuery } from '@chakra-ui/react';
import debounce from 'lodash/debounce';
import { useAppDispatch, useAppSelector } from 'app/store';
import { setThemePaletteAction } from 'app/store/Settings/slice';
import { isWebClient } from 'app/utils/helpers';
import { getMobileOperatingSystem } from 'app/utils/utils';
import { config } from 'config';
import {
  getScrollIsMounted,
  getThemeModeSelector,
} from 'app/store/Settings/selectors';
import { outsideScrollableContentClassName } from 'app/components/ScrollableContent';
import { channels } from 'shared/constants';
import { breakpoints } from 'app/constants';
import { ThemeMode } from 'types/enums';

export const useWindowSize = (debounceMs?: number) => {
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };
    const debouncedResizeHandler = debounce(handleResize, debounceMs || 500);

    window.addEventListener('resize', debouncedResizeHandler);
    return () => window.removeEventListener('resize', debouncedResizeHandler);
  }, [debounceMs]);

  return windowSize;
};

const getThemeMode = (isDark: boolean) =>
  isDark ? ThemeMode.DARK : ThemeMode.LIGHT;

const useWebAutoTheme = (isThemeAuto: boolean) => {
  const dispatch = useAppDispatch();
  const cssDarkThemeSelector = '(prefers-color-scheme: dark)';
  const mq = window.matchMedia(cssDarkThemeSelector);

  const changeThemePalette = useCallback(
    (evt: MediaQueryListEvent) =>
      dispatch(setThemePaletteAction(getThemeMode(evt.matches))),
    [dispatch]
  );

  useEffect(() => {
    if (isWebClient) {
      if (isThemeAuto) {
        dispatch(setThemePaletteAction(getThemeMode(mq.matches)));

        if (mq.addEventListener) {
          mq.addEventListener('change', changeThemePalette);
        }
      } else if (mq.removeEventListener) {
        mq.removeEventListener('change', changeThemePalette);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isThemeAuto]);
};

const useDesktopAutoTheme = (isThemeAuto: boolean) => {
  const dispatch = useAppDispatch();
  const { api } = window;
  useEffect(() => {
    if (config.isElectronBuild && isThemeAuto) {
      api.invoke(channels.IS_DARK_MODE).then((isDarkMode: boolean) => {
        dispatch(setThemePaletteAction(getThemeMode(isDarkMode)));
      });
      const removeListener = api.on(
        channels.TOGGLE_DARK_MODE,
        (type: ThemeMode) => {
          dispatch(setThemePaletteAction(type));
        }
      );
      return () => {
        removeListener();
      };
    }
    return undefined;
  }, [api, dispatch, isThemeAuto]);
};

export const useAutoTheme = () => {
  const themeMode = useAppSelector(getThemeModeSelector);
  const isThemeAuto = themeMode === ThemeMode.SYSTEM;
  useWebAutoTheme(isThemeAuto);
  useDesktopAutoTheme(isThemeAuto);

  useLayoutEffect(() => {
    // remove initial BG color set in index.html
    document.body.style.backgroundColor = '';
  }, []);
};

export const useApplyClassForStickyElement = (
  className: string,
  elementId: string,
  topPx: number
) => {
  useEffect(() => {
    const el = document.querySelector(`#${elementId}`);
    const observer = new IntersectionObserver(
      ([e]) => e.target.classList.toggle(className, e.intersectionRatio < 1),
      { threshold: [1], rootMargin: `${-topPx - 1}px 0px 0px 0px` }
    );
    if (el) {
      observer.observe(el);
    }
  }, [className, elementId, topPx]);
};

export const useIsElementVisibleFromTop = (
  ref: React.MutableRefObject<any>,
  topPx: number
) => {
  const [isVisible, setIsVisible] = useState(true);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([e]) => {
        setIsVisible(e.isIntersecting);
      },
      { threshold: [1], rootMargin: `${-topPx - 1}px 0px 0px 0px` }
    );
    if (ref?.current) {
      observer.observe(ref.current);
    }
    return () => observer.disconnect();
  }, [ref, topPx]);

  return [isVisible];
};

export const useIsElementVisible = (target: Element | null) => {
  const [isVisible, setIsVisible] = useState(false);

  const onVisibilityChange: IntersectionObserverCallback = (entries) => {
    setIsVisible(entries[0].isIntersecting);
  };

  useEffect(() => {
    if (!target) {
      return;
    }
    const observer = new IntersectionObserver(onVisibilityChange);
    observer.observe(target);
  }, [target]);

  return [isVisible];
};

export const useUniqueSvgId = (
  ref: RefObject<SVGElement>,
  uniqueId?: string
) => {
  useEffect(() => {
    if (ref.current) {
      const attributes = ['clip-path', 'fill', 'filter', 'mask'];
      const idElements = [...ref.current.querySelectorAll('[id]')];
      idElements.forEach((idElement) => {
        attributes.forEach((attribute) => {
          const value = `url(#${idElement.id})`;
          const selector = `[${attribute}="${value}"]`;

          [...(ref.current as SVGElement).querySelectorAll(selector)].forEach(
            (attributeElement) => {
              attributeElement.setAttribute(
                attribute,
                value.replace(idElement.id, `${uniqueId}-${idElement.id}`)
              );
            }
          );
        });
        idElement.setAttribute('id', `${uniqueId}-${idElement.id}`);
      });
    }
  }, [ref, uniqueId]);
};

export const useOutsideScrollableContentRef = () => {
  const scrollMounted = useAppSelector(getScrollIsMounted);
  const scrollableContent = document.querySelector(
    `.${outsideScrollableContentClassName}`
  );

  return scrollableContent && scrollMounted
    ? ({
        current: scrollableContent,
      } as RefObject<HTMLDivElement>)
    : undefined;
};

export const useIsMobile = (dependOnOS = true) => {
  const isMobileDevice = dependOnOS
    ? Boolean(getMobileOperatingSystem())
    : true;
  const [isMobileWidth] = useMediaQuery(
    `(max-width: ${breakpoints.mobileScreen - 1}px)`, // to exclude 768px width
    {
      ssr: false,
    }
  );
  const [isMobileHeight] = useMediaQuery(`(max-height: 485px)`, {
    ssr: false,
  });
  const isMobileDimensions = isMobileWidth || isMobileHeight;

  if (config.isElectronBuild) {
    return false;
  }

  return isMobileDimensions && isMobileDevice;
};

export const useClickedOutsideElement = <T extends HTMLElement>(
  action: (event: MouseEvent) => void = () => {}
) => {
  const ref = useRef<T>(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        action(event);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [action, ref]);

  return { ref };
};
